/**
 * LLM Provider Configuration Types
 * Supports multiple LLM providers with encrypted API key storage
 */

export type LLMProvider = 'deepseek' | 'zhipu' | 'moonshot' | 'qwen' | 'ernie' | 'hunyuan' | 'doubao' | 'openai' | 'anthropic' | 'google' | 'custom';

export interface LLMProviderConfig {
  id: string;
  provider: LLMProvider;
  name: string;
  baseUrl: string;
  model: string;
  apiKey: string; // Will be encrypted before storage
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LLMProviderTemplate {
  provider: LLMProvider;
  name: string;
  icon: string;
  baseUrl: string;
  defaultModel: string;
  models: string[];
  requiresApiKey: boolean;
  description: string;
  color: string;
}

// Supported LLM Providers
export const LLM_PROVIDERS: LLMProviderTemplate[] = [
  // Domestic Chinese LLM Providers
  {
    provider: 'deepseek',
    name: 'DeepSeek',
    icon: '🧠',
    baseUrl: 'https://api.deepseek.com/v1',
    defaultModel: 'deepseek-chat',
    models: ['deepseek-chat', 'deepseek-coder', 'deepseek-reasoner'],
    requiresApiKey: true,
    description: '深度求索 - 强大的AI模型，擅长编程、数学和推理',
    color: '#4F46E5'
  },
  {
    provider: 'zhipu',
    name: '智谱AI (Zhipu AI)',
    icon: '🔬',
    baseUrl: 'https://open.bigmodel.cn/api/paas/v4',
    defaultModel: 'glm-4-flash',
    models: ['glm-4-flash', 'glm-4-plus', 'glm-4-air', 'glm-4', 'glm-3-turbo'],
    requiresApiKey: true,
    description: '智谱AI - 清华大学出品，支持中英双语对话',
    color: '#0066FF'
  },
  {
    provider: 'moonshot',
    name: 'Moonshot (月之暗面)',
    icon: '🌙',
    baseUrl: 'https://api.moonshot.cn/v1',
    defaultModel: 'moonshot-v1-8k',
    models: ['moonshot-v1-8k', 'moonshot-v1-32k', 'moonshot-v1-128k'],
    requiresApiKey: true,
    description: '月之暗面 - 长上下文处理能力，支持128K上下文',
    color: '#7C3AED'
  },
  {
    provider: 'qwen',
    name: '通义千问 (Qwen)',
    icon: '💬',
    baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    defaultModel: 'qwen-turbo',
    models: ['qwen-turbo', 'qwen-plus', 'qwen-max', 'qwen-max-longcontext'],
    requiresApiKey: true,
    description: '阿里云通义千问 - 阿里巴巴开发的大型语言模型',
    color: '#FF6A00'
  },
  {
    provider: 'ernie',
    name: '文心一言 (ERNIE)',
    icon: '📝',
    baseUrl: 'https://aip.baidubce.com/rpc/2.0/ai_custom/v1/wenxinworkshop',
    defaultModel: 'ernie-bot-turbo',
    models: ['ernie-bot-turbo', 'ernie-bot', 'ernie-bot-4', 'ernie-speed'],
    requiresApiKey: true,
    description: '百度文心一言 - 百度知识增强大语言模型',
    color: '#2932E1'
  },
  {
    provider: 'hunyuan',
    name: '腾讯混元 (Hunyuan)',
    icon: '🎯',
    baseUrl: 'https://hunyuan.tencentcloudapi.com/v1',
    defaultModel: 'hunyuan-lite',
    models: ['hunyuan-lite', 'hunyuan-standard', 'hunyuan-pro', 'hunyuan-turbo'],
    requiresApiKey: true,
    description: '腾讯混元 - 腾讯开发的大语言模型，多模态能力',
    color: '#00B96B'
  },
  {
    provider: 'doubao',
    name: '豆包大模型 (Doubao)',
    icon: '🫘',
    baseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
    defaultModel: 'ep-20241105104626-wzjvb',
    models: ['ep-20241105104626-wzjvb', 'doubao-pro-4k', 'doubao-pro-32k', 'doubao-pro-128k', 'doubao-lite-4k'],
    requiresApiKey: true,
    description: '豆包大模型 - 字节跳动开发，强大的理解和生成能力',
    color: '#165DFF'
  },
  // International LLM Providers
  {
    provider: 'openai',
    name: 'OpenAI',
    icon: '🤖',
    baseUrl: 'https://api.openai.com/v1',
    defaultModel: 'gpt-4o-mini',
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    requiresApiKey: true,
    description: 'OpenAI - 业界领先的AI模型',
    color: '#10A37F'
  },
  {
    provider: 'anthropic',
    name: 'Claude',
    icon: '🎭',
    baseUrl: 'https://api.anthropic.com/v1',
    defaultModel: 'claude-3-5-sonnet-20241022',
    models: ['claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022', 'claude-3-opus-20240229'],
    requiresApiKey: true,
    description: 'Claude - 安全可靠的AI助手',
    color: '#D97757'
  },
  {
    provider: 'google',
    name: 'Gemini',
    icon: '🔍',
    baseUrl: 'https://generativelanguage.googleapis.com/v1',
    defaultModel: 'gemini-1.5-flash',
    models: ['gemini-1.5-pro', 'gemini-1.5-flash', 'gemini-pro'],
    requiresApiKey: true,
    description: 'Gemini - Google的生成式AI模型',
    color: '#4285F4'
  },
  {
    provider: 'custom',
    name: '自定义 (Custom)',
    icon: '⚙️',
    baseUrl: '',
    defaultModel: '',
    models: [],
    requiresApiKey: true,
    description: '配置您自己的LLM服务或兼容OpenAI格式的API',
    color: '#6B7280'
  }
];

// Platform default configuration (used when user hasn't configured their own API)
export const PLATFORM_DEFAULT_LLM: LLMProviderConfig = {
  id: 'platform-default',
  provider: 'deepseek',
  name: '平台默认',
  baseUrl: 'https://api.deepseek.com/v1',
  model: 'deepseek-chat',
  apiKey: '', // Empty - will use platform's default key
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};
